import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import {
  FaSearch,
  FaPlay,
  FaFilePdf,
  FaImage,
  FaCalendarAlt,
  FaUser,
  FaEye,
  FaUserGraduate,
  FaArrowRight,
  FaHome,
  FaCheckCircle
} from 'react-icons/fa';
import BotaoNaoEncontrou from '../components/BotaoNaoEncontrou';
import SemanaDropdown from '../components/SemanaDropdown';
import NaturezaDropdown from '../components/NaturezaDropdown';
import './MateriaisPorCategoria.css';
import { normalizeText } from '../utils/text';
import { getMaterialSubcategories } from '../utils/materialCategories';

function MateriaisPorCategoria() {
  const { nome } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [materiaisPorSubcategoria, setMateriaisPorSubcategoria] = useState({});
  const [subcategorias, setSubcategorias] = useState([]);
  const [busca, setBusca] = useState('');
  const [semanaFiltro, setSemanaFiltro] = useState('');
  const [naturezaFiltro, setNaturezaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalMateriais, setTotalMateriais] = useState(0);

  // Função para verificar se um link é YouTube
  const isYouTubeLink = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Função para verificar se um link é Vimeo
  const isVimeoLink = (url) => {
    if (!url) return false;
    return url.includes('vimeo.com');
  };

  // Função para obter a URL do material considerando linkRelacionado
  const getMaterialUrl = (material) => {
    // Se é material de aluno e tem linkRelacionado com YouTube/Vimeo, usar ele
    if (material.source === 'student' && material.linkRelacionado) {
      if (isYouTubeLink(material.linkRelacionado) || isVimeoLink(material.linkRelacionado)) {
        return material.linkRelacionado;
      }
    }

    // Caso contrário, usar materialUrl normal
    return material.materialUrl;
  };

  // Função para obter o tipo de material considerando linkRelacionado
  const getMaterialType = (material) => {
    const materialUrl = getMaterialUrl(material);

    // Se tem linkRelacionado de YouTube/Vimeo, definir tipo baseado na URL
    if (material.source === 'student' && material.linkRelacionado && materialUrl === material.linkRelacionado) {
      if (isYouTubeLink(materialUrl)) return 'YOUTUBE';
      if (isVimeoLink(materialUrl)) return 'VIMEO';
    }

    // Caso contrário, usar materialType original
    return material.materialType?.toUpperCase();
  };

  const getSemanaLabel = (material) => {
    const rawSemana = material?.semana || material?.curso;
    if (!rawSemana) return '';

    const semana = String(rawSemana).trim();
    if (!semana) return '';

    const match = semana.match(/semana\s*(\d+)/i);
    return match ? `Semana ${match[1]}` : semana;
  };

  useEffect(() => {
    const fetchMateriais = async () => {
      setLoading(true);
      try {
        const categoriesRef = collection(db, 'categories');
        const categoriesSnapshot = await getDocs(categoriesRef);
        let subcategoriasLista = [];
        let categoriaId = null;
        categoriesSnapshot.forEach(doc => {
          const data = doc.data();
          const categoryId = doc.id;
          const categoryName = data.name;
          if (normalizeText(categoryName) === normalizeText(nome)) {
            categoriaId = categoryId;
            subcategoriasLista = (data.subcategories || [])
              .map(sub => (typeof sub === 'string' ? sub : sub?.name))
              .filter(Boolean);
          }
        });
        setSubcategorias(subcategoriasLista);

        const materialsRef = collection(db, 'materials');
        const materialsSnapshot = await getDocs(materialsRef);
        const materialsData = materialsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: 'admin'
        }));

        const materiaisFiltrados = materialsData.filter(m =>
          getMaterialSubcategories(m, categoriaId, nome).length > 0
        );

        const studentMaterialsRef = collection(db, 'student_materials');
        const studentQuery = query(
          studentMaterialsRef,
          where('status', '==', 'aprovado')
        );
        // Uma falha na consulta complementar não deve esconder materiais do admin.
        const [studentResult] = await Promise.allSettled([getDocs(studentQuery)]);
        const studentDocs = studentResult.status === 'fulfilled'
          ? studentResult.value.docs.filter(doc =>
            getMaterialSubcategories(doc.data(), categoriaId, nome).length > 0
          )
          : [];
        if (studentResult.status === 'rejected') {
          console.error('⚠ Erro ao carregar materiais de alunos:', studentResult.reason);
        }

        const studentMaterials = studentDocs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            title: d.titulo,
            description: d.descricao,
            materialType: d.materialType,
            materialUrl: d.arquivoURL || d.videoUrl,
            linkRelacionado: d.linkRelacionado, // ADICIONADO: campo linkRelacionado
            coverImage: d.coverImage || d.arquivoURL || d.videoUrl,
            authorName: d.authorName,
            authorEmail: d.authorEmail,
            createdAt: d.createdAt,
            subcategory: d.subcategoria,
            category: d.categoria,
            categories: d.categories,
            source: 'student',
            conteudo: d.conteudo,
            resposta: d.resposta,
            videoResposta: d.videoResposta,
            hasThumbnail: d.hasThumbnail,
            isEspecialista: d.isEspecialista,
            semana: d.semana || d.curso || '',
            naturezaMaterial: d.naturezaMaterial
          };
        });

        const todosMateriais = [...materiaisFiltrados, ...studentMaterials];

        const materiaisAgrupados = {};
        (subcategoriasLista.length ? subcategoriasLista : ['Outros']).forEach(s => {
          materiaisAgrupados[s] = [];
        });

        for (const material of todosMateriais) {
          for (const sub of getMaterialSubcategories(material, categoriaId, nome)) {
            if (!materiaisAgrupados[sub]) materiaisAgrupados[sub] = [];
            materiaisAgrupados[sub].push({ ...material, subcategory: sub });
          }
        }

        Object.keys(materiaisAgrupados).forEach(sub => {
          materiaisAgrupados[sub].sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
          });
        });

        setMateriaisPorSubcategoria(materiaisAgrupados);
        setTotalMateriais(todosMateriais.length);
      } catch (error) {
        console.error('⚠ Erro ao carregar materiais:', error);
      } finally {
        setLoading(false);
      }
    };

    if (nome) fetchMateriais();
  }, [nome]);

  const isEspecialista = (material) => {
    if (material?.source === 'admin') return true;
    const especialistaEmails = [
      'admin@conecta.com', 'professor@conecta.com', 'especialista@conecta.com'
    ];
    const especialistaNomes = [
      'Conecta Admin', 'Conecta Admin Master', 'Professor', 'Especialista'
    ];
    return (
      especialistaEmails.includes(material?.authorEmail) ||
      especialistaNomes.includes(material?.authorName) ||
      material?.isEspecialista === true
    );
  };

  const getTypeIcon = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return <FaPlay />;
      case 'PDF':
      case 'DOCX':
      case 'PPTX':
        return <FaFilePdf />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <FaImage />;
      default:
        return <FaEye />;
    }
  };

  const getTypeBadgeClass = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return 'type-video';
      case 'PDF':
      case 'DOCX':
      case 'PPTX':
        return 'type-pdf';
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return 'type-image';
      default:
        return 'type-default';
    }
  };

  const formatDate = (dateData) => {
    if (!dateData) return 'Data não disponível';
    let date;
    if (dateData.seconds) {
      date = new Date(dateData.seconds * 1000);
    } else if (dateData.toDate) {
      date = dateData.toDate();
    } else if (typeof dateData === 'string') {
      date = new Date(dateData);
    } else {
      date = new Date(dateData);
    }
    return date.toLocaleDateString('pt-BR');
  };

  const handleVerTodosSubcategoria = (subcategoria) => {
    navigate(`/materiais/${encodeURIComponent(subcategoria)}`);
  };

  const handleVerConteudo = (material) => {
    navigate(`/conteudo/${material.id}`);
  };

  const normalizedBusca = normalizeText(busca);
  const normalizedSemanaFiltro = normalizeText(semanaFiltro);
  const normalizedNaturezaConteudo = normalizeText('Conteúdo');
  const normalizedNaturezaGabarito = normalizeText('Gabarito');
  const normalizedNaturezaResumo = normalizeText('Resumo');
  const normalizedNaturezaOutros = normalizeText('Outros');
  const normalizeNatureza = (value) => {
    const normalized = normalizeText(value || '');
    return normalized === normalizedNaturezaConteudo ? normalizedNaturezaGabarito : normalized;
  };
  const isNaturezaComSemana = (value) => {
    const normalized = normalizeNatureza(value);
    return (
      normalized === normalizedNaturezaGabarito ||
      normalized === normalizedNaturezaResumo ||
      normalized === normalizedNaturezaOutros
    );
  };
  const normalizedNaturezaFiltro = normalizeNatureza(naturezaFiltro);

  const materiaisFiltrados = {};
  Object.keys(materiaisPorSubcategoria).forEach(subcategoria => {
    const lista = materiaisPorSubcategoria[subcategoria] || [];
    const filtrada = lista.filter(material => {
      const matchesSearch =
        normalizeText(material.title || '').includes(normalizedBusca) ||
        normalizeText(material.description || '').includes(normalizedBusca) ||
        normalizeText(material.authorName || '').includes(normalizedBusca);
      const matchesWeek =
        !normalizedSemanaFiltro ||
        normalizeText(material.semana || '') === normalizedSemanaFiltro;
      const matchesNatureza =
        !normalizedNaturezaFiltro ||
        normalizeNatureza(material.naturezaMaterial || '') === normalizedNaturezaFiltro;
      return matchesSearch && matchesWeek && matchesNatureza;
    });
    if (filtrada.length) materiaisFiltrados[subcategoria] = filtrada;
  });

  if (loading) {
    return (
      <div className="materiais-categoria-page" data-scope="mpc">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando materiais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="materiais-categoria-page" data-scope="mpc">
      <header className="categoria-header">
        <h1 className="categoria-titulo">{nome}</h1>
        <p className="categoria-subtitulo">{totalMateriais} materiais disponíveis</p>
      </header>

      <div className="categoria-busca">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por título, descrição ou autor..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <div className="filter-container">
            <NaturezaDropdown
              value={naturezaFiltro}
              onChange={(value) => {
                setNaturezaFiltro(value);
                if (!isNaturezaComSemana(value)) setSemanaFiltro('');
              }}
            />
          </div>
          <div className="filter-container">
            <SemanaDropdown
              value={semanaFiltro}
              onChange={setSemanaFiltro}
              disabled={naturezaFiltro && !isNaturezaComSemana(naturezaFiltro)}
            />
          </div>
        </div>
      </div>

      <div className="categoria-content">
        {Object.keys(materiaisFiltrados).length > 0 ? (
          Object.entries(materiaisFiltrados).map(([subcategoria, materiais]) => (
            <section key={subcategoria} className="subcategoria-section">
              <div className="subcategoria-header">
                <h2 className="subcategoria-titulo">{subcategoria}</h2>
                <div className="subcategoria-actions">
                  <span className="materiais-count">{materiais.length} materiais</span>
                  <button
                    className="ver-todos-button"
                    onClick={() => handleVerTodosSubcategoria(subcategoria)}
                  >
                    Ver todos <FaArrowRight />
                  </button>
                </div>
              </div>

              <div className="materiais-grid">
                {materiais.slice(0, 6).map((material) => {
                  const materialType = getMaterialType(material); // USAR função que considera linkRelacionado
                  const materialUrl = getMaterialUrl(material); // USAR função que considera linkRelacionado

                  const coverImage = (function () {
                    const previewImage = material.coverImage || material.thumbnail;
                    if (previewImage && previewImage !== materialUrl && material.hasThumbnail) return previewImage;
                    if (previewImage && previewImage !== materialUrl) return previewImage;
                    if (materialType === 'YOUTUBE' && materialUrl) {
                      const videoId = materialUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
                      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
                    }
                    if (materialType === 'VIMEO' && materialUrl) {
                      const vimeoId = materialUrl.match(/vimeo\.com\/(\d+)/)?.[1];
                      return vimeoId ? `https://i.vimeocdn.com/video/${vimeoId}_640x360.jpg` : null;
                    }
                    if (['PNG', 'JPG', 'JPEG'].includes(materialType) && materialUrl) return materialUrl;
                    return null;
                  })();

                  const verificado = isEspecialista(material);
                  const semanaLabel = getSemanaLabel(material);

                  return (
                    <div className="material-card" key={`${material.source}-${material.id}`}>
                      <div className="cover-wrapper">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={material.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.nextSibling;
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        <div
                          className={`cover-placeholder ${getTypeBadgeClass(materialType)}`}
                          style={{ display: coverImage ? 'none' : 'flex' }}
                        >
                          <div className="placeholder-icon">
                            {getTypeIcon(materialType)}
                          </div>
                          <div className="placeholder-text">
                            <span className="material-type">{materialType || 'CONTEÚDO'}</span>
                            <span className="material-title">{material.title}</span>
                          </div>
                        </div>

                        <div className={`type-badge ${getTypeBadgeClass(materialType)}`}>
                          {getTypeIcon(materialType)}
                          <span>{materialType}</span>
                        </div>

                        {material.source === 'student' && !verificado && (
                          <div className="student-badge">
                            <FaUserGraduate />
                            <span>Material do Aluno</span>
                          </div>
                        )}

                        {verificado && (
                          <div className="expert-badge">
                            <FaCheckCircle />
                            <span>Especialista</span>
                          </div>
                        )}

                        {/* Indicador de Link Relacionado para materiais de aluno */}
                        {material.source === 'student' && material.linkRelacionado && (
                          <div
                            className="link-badge"
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '8px',
                              background: 'rgba(156, 39, 176, 0.9)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              zIndex: 2
                            }}
                          >
                            🔗 Link
                          </div>
                        )}

                        <div className="hover-overlay">
                          <button
                            className="preview-button"
                            onClick={() => handleVerConteudo(material)}
                          >
                            <FaEye />
                            Ver Conteúdo
                          </button>
                        </div>
                      </div>

                      <div className="material-info">
                        <h3 className="material-title">{material.title}</h3>
                        {semanaLabel && (
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.22rem 0.6rem',
                              borderRadius: '9999px',
                              fontSize: '0.66rem',
                              fontWeight: '800',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              whiteSpace: 'nowrap',
                              background: 'rgba(250, 204, 21, 0.95)',
                              color: '#111827',
                              border: '1px solid rgba(250, 204, 21, 0.55)',
                              boxShadow: '0 2px 8px rgba(250, 204, 21, 0.2)',
                              width: 'fit-content'
                            }}
                            title={`Conteudo referente a ${semanaLabel}`}
                          >
                            <span>{semanaLabel}</span>
                          </div>
                        )}

                        <div className="material-meta">
                          <div className="meta-item">
                            <FaUser className="meta-icon" />
                            <span>{material.authorName || 'Conecta Admin'}</span>
                            {material.source === 'student' && !verificado && (
                              <span className="author-type">(Aluno)</span>
                            )}
                            {verificado && (
                              <span className="author-type-expert">
                                <span className="dot"></span>
                                (Especialista)
                              </span>
                            )}
                            {material.source !== 'student' && !verificado && (
                              <span className="author-type-expert">
                                <span className="dot"></span>
                                Especialista
                              </span>
                            )}
                          </div>

                          <div className="meta-item">
                            <FaCalendarAlt className="meta-icon" />
                            <span>{formatDate(material.createdAt)}</span>
                          </div>
                        </div>

                        <p className="material-description">
                          {material.description?.length > 80
                            ? `${material.description.substring(0, 80)}...`
                            : material.description || 'Sem descrição disponível'}
                        </p>

                        <button
                          className="ver-conteudo-button"
                          onClick={() => handleVerConteudo(material)}
                        >
                          <FaEye />
                          Ver Conteúdo
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {materiais.length > 6 && (
                <div className="ver-mais-container">
                  <button
                    className="ver-mais-button"
                    onClick={() => handleVerTodosSubcategoria(subcategoria)}
                  >
                    Ver mais {materiais.length - 6} materiais de {subcategoria}
                  </button>
                </div>
              )}
            </section>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaSearch />
            </div>
            <h3>Nenhum material encontrado</h3>
            <p>
              {busca
                ? `Não encontramos materiais com "${busca}" nesta categoria.`
                : 'Esta categoria ainda não possui materiais disponíveis.'}
            </p>

            <BotaoNaoEncontrou termoBusca={busca || nome} />
          </div>
        )}
      </div>
    </div>
  );
}

export default MateriaisPorCategoria;
